import {useApps} from '@cohort/merchants/apps/useApps';
import Button from '@cohort/merchants/components/buttons/Button';
import SelectInput from '@cohort/merchants/components/form/v2/SelectInput';
import type {SelectOption} from '@cohort/merchants/components/form/v2/SelectPicker';
import Loader from '@cohort/merchants/components/Loader';
import {useConnections} from '@cohort/merchants/hooks/api/Connections';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import EditConnectionModal from '@cohort/merchants/pages/connections/new/EditConnectionModal';
import type {PerkFormValues} from '@cohort/merchants/pages/perks/perk/edit/utils';
import type {AppId, MerchantConnectorId} from '@cohort/shared/schema/apps';
import {getAppSpec} from '@cohort/shared/schema/apps';
import type {AppStruct} from '@cohort/shared/schema/apps/app';
import {useEffect, useState} from 'react';
import type {Control, UseFormRegister} from 'react-hook-form';
import {useController} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {isNil} from 'remeda';

export type ConnectionPickerProps<T extends AppStruct> = {
  appId: T['Id'];
  register: UseFormRegister<PerkFormValues>;
  control: Control<PerkFormValues>;
  onConnectionModalClose?: () => void;
};

export const ConnectionPicker = <T extends AppStruct>({
  appId,
  register,
  control,
  onConnectionModalClose,
}: ConnectionPickerProps<T>): React.ReactElement => {
  const merchant = useCurrentMerchant();
  const [createConnectionModalOpen, setCreateConnectionModalOpen] = useState(false);
  const {t} = useTranslation('components', {
    keyPrefix: 'connections.connectionPicker',
  });
  const appSpec = getAppSpec(appId);
  const {merchantConnectionCreationDisabled} = useApps();

  const hideCreateConnectionButton = merchantConnectionCreationDisabled(appSpec.id as AppId);

  if (appSpec.merchantConnector === null) {
    throw new Error(`App ${appId} does not support merchant connections`);
  }

  const {data: connections} = useConnections(merchant.id, {
    connectorId: [appSpec.merchantConnector.id as MerchantConnectorId],
  });
  const readyConnections = connections?.filter(connection => connection.status === 'ready');

  const {field: connectionIdField} = useController({name: 'integration.connectionId', control});

  useEffect(() => {
    if (
      isNil(connectionIdField.value) &&
      readyConnections !== undefined &&
      readyConnections.length > 0
    ) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      connectionIdField.onChange(readyConnections[0]!.id);
    }
  }, [connectionIdField, readyConnections]);

  if (!readyConnections) {
    return (
      <div className="space-y-6">
        <Loader size={30} color="gray" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {readyConnections.length > 0 && (
        <SelectInput
          name="integration.connectionId"
          label={t('labelConnection')}
          register={register}
          control={control}
          options={readyConnections.map(
            connection =>
              ({
                label: connection.name,
                value: connection.id,
                isDisabled: connection.status !== 'ready',
              }) satisfies SelectOption
          )}
        />
      )}
      {!hideCreateConnectionButton && (
        <Button
          data-testid="create-connection-btn"
          onClick={() => {
            setCreateConnectionModalOpen(true);
          }}
        >
          {t('buttonCreateConnection')}
        </Button>
      )}
      {createConnectionModalOpen && (
        <EditConnectionModal
          onClose={() => {
            setCreateConnectionModalOpen(false);
            onConnectionModalClose?.();
          }}
          appId={appId}
          existingConnection={undefined}
        />
      )}
    </div>
  );
};

export default ConnectionPicker;
